import { Component, type ErrorInfo, type ReactNode } from "react";
import i18next from "i18next";
import { createLogger } from "../logger";

const log = createLogger("core:error-boundary");

interface ErrorBoundaryProps {
  /** Identifies which boundary caught the error, for log correlation. */
  label: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Class-based render error boundary. Logs via the @core logger (→ prod error sink seam) and
 * shows a token-styled, i18n fallback. i18n is used only when ready — the boundary may catch
 * an error thrown DURING i18n init, so it must never depend on it.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error("Unhandled render error caught by boundary", {
      context: "error-boundary",
      boundary: this.props.label,
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  private readonly handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // i18n-guarded: use i18n only when ready (the boundary may catch an error thrown during
    // i18n init). defaultValue doubles as the fallback if a key is somehow missing. Keys are
    // passed as strings (not literals) since "errors.*" resolves via fallbackNS at runtime.
    const ready = i18next.isInitialized;
    const tr = (key: string, fallback: string): string =>
      ready ? i18next.t(key, { defaultValue: fallback }) : fallback;
    const heading = tr("errors.unknown.title", "Une erreur est survenue");
    const retryLabel = tr("actions.retry", "Réessayer");
    const reloadLabel = tr("actions.reload", "Recharger la page");

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-lg bg-surface-page px-md text-center">
        <h1 className="text-lg-semibold text-[var(--text-primary)]">
          {heading}
        </h1>
        <div className="flex gap-sm">
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-md bg-brand-700 px-md py-xs text-sm-semibold text-white hover:bg-brand-800"
          >
            {retryLabel}
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-md border border-[var(--action-secondary-border)] bg-[var(--surface-card)] px-md py-xs text-sm-semibold text-[var(--action-secondary-fg)]"
          >
            {reloadLabel}
          </button>
        </div>
      </div>
    );
  }
}
